import {
  OPERATORS_BY_TYPE,
  WITHIN_FIELD,
  WITHIN_LABEL,
  defaultOperatorFor,
  isRuleComplete,
  newRule,
  type Combinator,
  type FieldType,
  type FilterRule,
} from '../lib/filters';

interface FilterBuilderProps {
  rules: FilterRule[];
  combinator: Combinator;
  availableFields: string[];
  fieldTypes: Record<string, FieldType>;
  /** Field id → label (from the CRM); ids without one show as-is. */
  fieldLabels?: Record<string, string>;
  onChange: (rules: FilterRule[]) => void;
  onCombinatorChange: (c: Combinator) => void;
  onClear: () => void;
}

export default function FilterBuilder({
  rules,
  combinator,
  availableFields,
  fieldTypes,
  fieldLabels = {},
  onChange,
  onCombinatorChange,
  onClear,
}: FilterBuilderProps) {
  const typeOf = (f: string): FieldType => (f === WITHIN_FIELD ? 'distance' : fieldTypes[f] ?? 'string');
  const labelOf = (f: string): string => (f === WITHIN_FIELD ? WITHIN_LABEL : fieldLabels[f] ? `${fieldLabels[f]} (${f})` : f);
  const fieldOptions = [WITHIN_FIELD, ...availableFields.filter((f) => f !== WITHIN_FIELD)];
  const firstField = availableFields[0] ?? 'name';

  function updateRule(idx: number, patch: Partial<FilterRule>) {
    const next = [...rules];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  }

  function removeRule(idx: number) {
    onChange(rules.filter((_, i) => i !== idx));
  }

  function addRule() {
    onChange([...rules, newRule(firstField, typeOf(firstField))]);
  }

  function changeField(idx: number, field: string) {
    const type = typeOf(field);
    const ops = OPERATORS_BY_TYPE[type];
    const currentRule = rules[idx];
    const keepOp = ops.some((o) => o.op === currentRule.operator);
    updateRule(idx, {
      field,
      operator: keepOp ? currentRule.operator : defaultOperatorFor(type),
      value: '',
      center: undefined,
    });
  }

  /** "lat, lng" typed by hand (or pasted from Google Maps) → the radius rule's centre. */
  function setCenter(idx: number, text: string) {
    const m = text.split(',').map((x) => Number(x.trim()));
    const center = m.length === 2 && Number.isFinite(m[0]) && Number.isFinite(m[1]) ? { lat: m[0], lng: m[1] } : undefined;
    updateRule(idx, { center });
  }

  return (
    <div className="flex flex-col gap-2">
      {rules.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Match:</span>
          <div className="inline-flex border border-gray-300 overflow-hidden">
            {(['AND', 'OR'] as const).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onCombinatorChange(c)}
                className={`px-2 py-0.5 text-xs ${
                  combinator === c
                    ? 'bg-brand-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {c === 'AND' ? 'all' : 'any'}
              </button>
            ))}
          </div>
          <span className="text-xs text-gray-500">
            {combinator === 'AND' ? 'of these conditions' : 'of these conditions'}
          </span>
        </div>
      )}

      {rules.map((rule, idx) => {
        const type = typeOf(rule.field);
        const ops = OPERATORS_BY_TYPE[type];
        const opDef = ops.find((o) => o.op === rule.operator) ?? ops[0];
        const inputType = type === 'number' ? 'number' : type === 'date' ? 'date' : 'text';

        return (
          <div key={rule.id} className="flex flex-wrap items-center gap-2">
            <select
              value={rule.field}
              onChange={(e) => changeField(idx, e.target.value)}
              className="border border-gray-300 bg-white px-2 py-1 text-xs focus:border-brand-500 focus:outline-none min-w-40"
            >
              {fieldOptions.map((f) => (
                <option key={f} value={f}>
                  {labelOf(f)}
                </option>
              ))}
            </select>
            <select
              value={rule.operator}
              onChange={(e) =>
                updateRule(idx, { operator: e.target.value as FilterRule['operator'] })
              }
              className="border border-gray-300 bg-white px-2 py-1 text-xs focus:border-brand-500 focus:outline-none"
            >
              {ops.map((o) => (
                <option key={o.op} value={o.op}>
                  {o.label}
                </option>
              ))}
            </select>
            {opDef.needsValue && (
              <input
                type={inputType}
                value={rule.value}
                onChange={(e) => updateRule(idx, { value: e.target.value })}
                placeholder={type === 'distance' ? 'miles' : rule.operator === 'isOneOf' || rule.operator === 'isNotOneOf' ? 'a, b, c' : 'value'}
                className={`border border-gray-300 bg-white px-2 py-1 text-xs focus:border-brand-500 focus:outline-none ${type === 'distance' ? 'w-20' : 'flex-1 min-w-32'}`}
              />
            )}
            {type === 'distance' && (
              <>
                <span className="text-xs text-gray-500">of</span>
                <input
                  type="text"
                  defaultValue={rule.center ? `${rule.center.lat}, ${rule.center.lng}` : ''}
                  onBlur={(e) => setCenter(idx, e.target.value)}
                  placeholder="lat, lng"
                  title="Centre as latitude, longitude (paste from Google Maps)"
                  className={`border bg-white px-2 py-1 text-xs focus:border-brand-500 focus:outline-none flex-1 min-w-40 ${rule.center ? 'border-gray-300' : 'border-amber-400'}`}
                />
              </>
            )}
            <button
              type="button"
              onClick={() => removeRule(idx)}
              aria-label="Remove filter"
              className="text-gray-400 hover:text-red-600 px-1 text-sm"
              title="Remove"
            >
              &times;
            </button>
          </div>
        );
      })}

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={addRule}
          className="border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-brand-50 hover:border-brand-300"
        >
          + Add filter
        </button>
        {rules.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-gray-500 hover:text-gray-800"
          >
            Clear all
          </button>
        )}
        {rules.length > 0 && (
          <span className="text-xs text-gray-400 ml-auto">
            {rules.filter(isRuleComplete).length} active
          </span>
        )}
      </div>
    </div>
  );
}
